import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/shared/api/api-client';
import type { ProviderBillingSettings } from '@/types/settings-api';

/**
 * Canonical React Query key for the provider-billing-settings cache slot.
 * Exported so the save mutation, stories, and tests can target the same
 * slot — eliminates magic-string coupling across consumers (same pattern
 * as `practitionerByIdQueryKey` per @samer PR #132 review).
 */
export function providerBillingSettingsQueryKey(
  doctorSetupId: string | null | undefined,
): readonly [string, string, string | null | undefined] {
  return ['settings', 'provider-billing-settings', doctorSetupId];
}

/**
 * Per-practitioner billing settings — PROD-763 Billing tab (read-only PR-A).
 *
 * GET /api/Billing/ProviderSettings/{doctorSetupId}
 *
 * Returns the practitioner's billing profiles + total service count.
 * Flutter source: `settings_data_source_impl.dart:1307`
 * (`fetchProviderBillingSettings`).
 *
 * Wire-shape verified against working dev cURL 2026-05-15. See
 * `ProviderBillingSettings` in `settings-api.ts` for the full envelope.
 *
 * Surfaced ONLY when the user has the `Billing_Settings` view
 * permission — the tab is gated in the practitioner-edit shell (per
 * Flutter `doctor_base_widget.dart:230-242`); this hook does not
 * enforce the gate itself.
 */
export function useProviderBillingSettings(
  doctorSetupId: string | null | undefined,
) {
  return useQuery({
    queryKey: providerBillingSettingsQueryKey(doctorSetupId),
    queryFn: ({ signal }) => {
      if (!doctorSetupId) {
        throw new Error(
          'useProviderBillingSettings called without doctorSetupId',
        );
      }
      return get<ProviderBillingSettings>(
        `/api/Billing/ProviderSettings/${doctorSetupId}`,
        { signal },
      );
    },
    enabled: !!doctorSetupId,
  });
}

/**
 * Save the full per-practitioner billing settings envelope —
 * PROD-763 PR-B (Add / Edit / Archive flows).
 *
 * POST /api/Billing/ProviderSettings
 *
 * Single endpoint handles BOTH add and update — backend branches on
 * `billingProfiles[i].id` (null = insert, GUID = update). Verified
 * against the live dev save cURL 2026-05-15 — the response body is
 * the full `ProviderBillingSettings` with newly-allocated IDs filled
 * in on previously-null rows.
 *
 * Array-replace semantics on the server: every profile's `updatedOn`
 * touches on each save, not just the changed one. So we always POST
 * the full `billingProfiles[]` array; the consumer (Edit dialog +
 * Archive flow) mutates the local draft in-place and dispatches.
 *
 * **POST response shape is symmetric with the GET response** — same
 * top-level envelope (`{servicesCount, providerId, billingProfiles[]}`)
 * and same keys on every nested profile / service / visitFee. Verified
 * by diffing the captured GET cURL response (2026-05-15) against the
 * captured save-response cURL (also 2026-05-15). The only difference
 * is that previously-null `id` fields on new rows are populated with
 * server-allocated GUIDs.
 *
 * Because the shapes match exactly, writing the POST response back to
 * the cache via `setQueryData(getQueryKey, response)` is safe — readers
 * (`useProviderBillingSettings`) see the same shape they'd get from a
 * fresh GET. No normaliser layer needed.
 *
 * If a future schema change introduces a write-only field or a shape
 * divergence between POST response and GET response, **convert to
 * `invalidateQueries` instead of `setQueryData`** so the cache is
 * refilled from a fresh GET. Per @samer PR #136 review item 2.
 *
 * Wire-shape contract (see settings-api.ts for the full type block):
 *   - `id: null` on a new profile / service / visitFee survives
 *     JSON.stringify (only `undefined` is stripped).
 *   - `isDeleted: true` + `isArchived: true` on a profile triggers
 *     the archive path server-side.
 *   - Datetime strings preserve their no-Z form verbatim on
 *     round-trip; never parse before sending.
 */
export function useSaveProviderBillingSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settings: ProviderBillingSettings) =>
      post<ProviderBillingSettings>(
        '/api/Billing/ProviderSettings',
        settings,
      ),
    onSuccess: (response, vars) => {
      // POST response is shape-identical to GET (verified — see
      // header doc above). Direct setQueryData is safe.
      queryClient.setQueryData(
        providerBillingSettingsQueryKey(vars.providerId),
        response,
      );
    },
  });
}
